import React from "react";
import { cn } from "@/lib/utils";

/**
 * The one divider between panels: 1 px visible, 10 px hit-area, col-resize. Drag to size, ←/→ for 24 px steps,
 * Home / double-click resets, dragging well past the minimum collapses the panel when `onCollapse` is given.
 * `edge` says which side of the panel the handle sits on; `rtl` flips the drag direction for right-hand panels.
 */
export function ResizeHandle({ value, min, max, onResize, onReset, onCollapse, rtl = false, edge = "right", label, testId, className }) {
  const start = (e) => {
    e.preventDefault();
    const x0 = e.clientX;
    const w0 = value;
    let collapsed = false;
    const move = (ev) => {
      const d = (ev.clientX - x0) * (rtl ? -1 : 1);
      const next = w0 + d;
      if (onCollapse && next < min - 80) { if (!collapsed) { collapsed = true; onCollapse(); up(); } return; }
      onResize(Math.min(max, Math.max(min, next)));
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      document.body.removeAttribute("data-resizing");
    };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.body.setAttribute("data-resizing", "1");
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };
  const keys = (e) => {
    const step = e.shiftKey ? 64 : 24;
    if (e.key === "ArrowLeft" || e.key === "ArrowRight") { e.preventDefault(); const dir = (e.key === "ArrowRight" ? 1 : -1) * (rtl ? -1 : 1); onResize(Math.min(max, Math.max(min, value + dir * step))); }
    else if (e.key === "Home") { e.preventDefault(); onReset?.(); }
  };
  return (
    <div role="separator" aria-orientation="vertical" aria-label={label || "Endre bredde"} aria-valuemin={min} aria-valuemax={max} aria-valuenow={value} aria-valuetext={`${value} piksler`} tabIndex={0}
      onPointerDown={start} onDoubleClick={onReset} onKeyDown={keys} title="Dra for å endre bredde · dobbeltklikk for standard"
      className={cn("group absolute inset-y-0 z-30 w-[10px] cursor-col-resize outline-none", edge === "right" ? "-right-[5px]" : "-left-[5px]", className)} data-testid={testId}>
      <div className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-black/[0.07] transition-colors duration-150 group-hover:bg-black/[0.18] group-focus-visible:bg-primary group-active:bg-primary" />
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-9 w-[3px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-black/[0.22] opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-visible:opacity-100" />
    </div>
  );
}
